/**
 * Native generation input v1. The old wizard-schemas describe unversioned
 * native forms, not the web generation request. Keep those schemas intact so
 * saved answers can be recognised and reviewed before any conversion.
 *
 * This is deliberately a small supported subset of the web request. Add a
 * field only after checking its actual render path in the web generator.
 * generationDraftVersion identifies saved form shape only. Durable native
 * request/attempt identity belongs to a separate protocol and is not active
 * in the current draft-only mobile endpoint.
 */
import { z } from "zod";
import { INT32_MAX, countSchema, entityIdSchema } from "../common";

export const GENERATION_DRAFT_VERSION = 1 as const;

const nullableText = z.string().nullable().optional();

export const AuthoritativeProjectFactsV1Schema = z.object({
  title: z.string().min(1),
  siteAddress: z.string().min(1),
  scopeDescription: nullableText,
  domesticClient: z.boolean().nullable().optional(),
  estimatedDuration: nullableText,
  contractorCount: countSchema.min(1).nullable().optional(),
}).strict();
export type AuthoritativeProjectFactsV1 = z.infer<typeof AuthoritativeProjectFactsV1Schema>;

const draftHeader = { generationDraftVersion: z.literal(GENERATION_DRAFT_VERSION) };

export const CanonicalGenerationDraftV1Schema = z.discriminatedUnion("documentType", [
  z.object({
    ...draftHeader,
    documentType: z.literal("COSHH"),
    // null explicitly requests server/default printed-approver resolution.
    // A manual name is retained when no governing default overrides it.
    answers: z.object({
      preparedBy: nullableText,
      approvedBy: z.string().trim().min(1).nullable(),
    }).strict(),
  }).strict(),
  z.object({
    ...draftHeader,
    documentType: z.literal("SITE_INDUCTION"),
    answers: z.object({ reportingProcedures: nullableText }).strict(),
  }).strict(),
  z.object({
    ...draftHeader,
    documentType: z.literal("EMERGENCY_PLAN"),
    // Site safety details remain current Project facts. Emergency contacts
    // are an editable document field in the web wizard, with a direct render
    // destination; old native contact strings still need explicit review.
    answers: z.object({ emergencyContacts: nullableText }).strict(),
  }).strict(),
  z.object({
    ...draftHeader,
    documentType: z.literal("CPP_LIGHTWEIGHT"),
    // Old inline risk/welfare answers can write back to Project facts in the
    // current web route; clientName can be masked by the Project value.
    answers: z.object({ preparedBy: nullableText }).strict(),
  }).strict(),
  z.object({
    ...draftHeader,
    documentType: z.literal("RAMS"),
    // Hazards: exact library slugs plus `custom-<id>` account-custom
    // pseudo-slugs — both resolved tenant-scoped server-side (a slug that
    // stopped being active simply drops out of the register). Project custom
    // hazards are auto-included server-side and are deliberately NOT
    // client-selectable. Personnel mirrors the web wizard's selectedWorkers
    // shape, but only each worker's `id` is honoured: the server re-resolves
    // every id against the project's CURRENT worker rows and prints their
    // name/role/company/First Aid/Fire Warden, dropping ids no longer on the
    // project (lib/rams-context.ts resolveRamsPersonnel). The other fields
    // are a display cache of what the user picked, never printed.
    // supervisorName is the works supervisor the user typed for this RAMS —
    // null until they change the field the editor seeds from the project.
    // The server resolves ONE supervisor from it (null/blank = the project's)
    // for the cover row, the personnel star and "Approved by" (after the
    // #2595 printed default approver), and writes a changed name back to the
    // project in the version's own transaction, as the web wizard's PATCH
    // does. So the payload carries no approvedBy of its own for RAMS.
    // Sequence-of-works tasks are NOT in the
    // draft: they are project state (library + custom trade tasks) the server
    // reads itself, and a RAMS refuses to generate with zero tasks.
    answers: z.object({
      selectedHazardSlugs: z.array(z.string().trim().min(1)),
      selectedWorkers: z
        .array(
          z.object({
            id: z.string().min(1),
            name: z.string().min(1),
            company: z.string().nullable().optional(),
            role: z.string().min(1),
            phone: z.string().nullable().optional(),
            firstAidTrained: z.boolean(),
            fireWardenTrained: z.boolean().optional(),
          }).strict(),
        ),
      supervisorName: nullableText,
    }).strict(),
  }).strict(),
  z.object({
    ...draftHeader,
    documentType: z.literal("CPP"),
    // Full CPP: exactly the fields the web wizard collects
    // (components/documents/cpp-steps) - site management/access, risk & safety
    // and welfare/emergency text fields. The core persists them to the Project
    // row (cppFieldNames write-back) exactly as the web does, so the user is
    // authoring project facts through the same reviewed path. Site rules,
    // key risks, welfare and emergency content are deliberately absent: the
    // web wizard stopped collecting them (single source = project settings).
    // Site photos are a separate surface (#2340 family) - imageIds is not
    // part of this draft: the native generation adapter carries the web
    // wizard's selection itself (lib/documents/cpp-regeneration-prefill.ts).
    answers: z.object({
      // Web collects this in the wizard (seeded from the signed-in user's
      // name) and the template prints it in the sign-off block.
      preparedBy: nullableText,
      // A typed approver only (#2821 follow-up review). null asks the server
      // to resolve the printed default approver (#2595), which also wins over
      // a typed name; the editor never echoes that displayed default back.
      approvedBy: nullableText,
      principalContractor: nullableText,
      principalDesigner: nullableText,
      principalDesignerCompany: nullableText,
      principalDesignerPhone: nullableText,
      principalDesignerEmail: nullableText,
      architectName: nullableText,
      architectCompany: nullableText,
      structuralEngineer: nullableText,
      clientContactName: nullableText,
      clientContactPhone: nullableText,
      clientContactEmail: nullableText,
      clientAddress: nullableText,
      startDate: nullableText,
      siteAccessArrangements: nullableText,
      deliveryArrangements: nullableText,
      publicProtection: nullableText,
      siteHoarding: nullableText,
      safetyArrangements: nullableText,
      trainingRequirements: nullableText,
      asbestosManagement: nullableText,
      permitToWork: nullableText,
      environmentalManagement: nullableText,
    }).strict(),
  }).strict(),
]);
export type CanonicalGenerationDraftV1 = z.infer<typeof CanonicalGenerationDraftV1Schema>;

/**
 * Full-CPP answers the generator prints ONLY from the request. No Project
 * column backs them (startDate, preparedBy and a typed approvedBy print from
 * the request, not the Project row), so a regeneration keeps each one only
 * when the editor carries it forward from the current version's stored
 * payload, as the website wizard's prefill does. Every other full-CPP answer
 * is written back to the Project and prints from there when left blank
 * (#2821 follow-up). approvedBy carries only a name someone typed: a printed
 * default approver's (#2595) is re-resolved at generation, never carried.
 */
export const CPP_DOCUMENT_ONLY_FIELDS = [
  "preparedBy", "approvedBy", "startDate", "publicProtection",
  "asbestosManagement", "permitToWork", "environmentalManagement",
] as const;
export type CppDocumentOnlyField = (typeof CPP_DOCUMENT_ONLY_FIELDS)[number];

const cppDocumentOnlyAnswers = {
  preparedBy: z.string().nullable(),
  approvedBy: z.string().nullable(),
  startDate: z.string().nullable(),
  publicProtection: z.string().nullable(),
  asbestosManagement: z.string().nullable(),
  permitToWork: z.string().nullable(),
  environmentalManagement: z.string().nullable(),
} satisfies Record<CppDocumentOnlyField, z.ZodTypeAny>;

/**
 * What the CURRENT full-CPP version printed for those answers, read by the
 * native editor before a regeneration. A field in `unrecorded` was never
 * stored with that version (plans made before these answers were kept): it
 * is UNKNOWN, not blank, and the editor must ask for it rather than silently
 * drop the section it printed. Its `answers` value is then null.
 *
 * `createdAt` stamps the version: a saved editor draft older than it predates
 * the version and is not resumed over it without asking.
 */
export const CppCurrentVersionPrefillV1Schema = z.object({
  documentId: entityIdSchema,
  versionId: entityIdSchema,
  versionNumber: z.number().int().min(1).max(INT32_MAX),
  createdAt: z.string().datetime(),
  answers: z.object(cppDocumentOnlyAnswers),
  // Open on the wire (#2411): a field a newer server starts keeping must not
  // fail an installed editor closed. The editor ignores names it does not
  // know; the server boundary (Strict below) emits only known fields.
  unrecorded: z.array(z.string()),
});
export type CppCurrentVersionPrefillV1 = z.infer<typeof CppCurrentVersionPrefillV1Schema>;

/**
 * The printed "Approved by" a FULL CPP generation resolves (#2595): the
 * account's default approver's name, shown read-only and never sent back as
 * typed input, or null when the rule does not apply; `warning` names an
 * approver with no name on their profile.
 */
const cppPrintedApproverFields = {
  name: z.string().nullable(),
  warning: z.string().nullable(),
};

/**
 * The full-CPP generation preflight's prefill fields. REQUIRED on the wire so
 * an older server fails the native editor closed rather than opening it blank
 * over a version it could not read. `cppCurrentVersion` null means no
 * full-CPP version exists; `cppPhotoCount` is how many site photos a native
 * regeneration will include (the current version's selection plus every
 * photo flagged for the CPP since - the web wizard's merge).
 */
export const CppRegenerationPrefillResponseV1Schema = z.object({
  cppCurrentVersion: CppCurrentVersionPrefillV1Schema.nullable(),
  cppPhotoCount: countSchema,
  cppPrintedApprover: z.object(cppPrintedApproverFields),
});
export type CppRegenerationPrefillResponseV1 = z.infer<typeof CppRegenerationPrefillResponseV1Schema>;

/** Server-boundary variant (#2411): the wire base stays permissive for installed builds. */
export const CppCurrentVersionPrefillV1Strict = CppCurrentVersionPrefillV1Schema.extend({
  answers: z.object(cppDocumentOnlyAnswers).strict(),
  unrecorded: z.array(z.enum(CPP_DOCUMENT_ONLY_FIELDS)),
}).strict();

/** Server-boundary variant of the whole prefill (#2411). */
export const CppRegenerationPrefillResponseV1Strict = z.object({
  cppCurrentVersion: CppCurrentVersionPrefillV1Strict.nullable(),
  cppPhotoCount: countSchema,
  cppPrintedApprover: z.object(cppPrintedApproverFields).strict(),
}).strict();

/** Strict native subset; the actual web validator is asserted in root tests. */
export const CanonicalGenerationPayloadSubsetV1Schema = z.object({
  confirmedProjectData: AuthoritativeProjectFactsV1Schema,
  selectedHazardSlugs: z.array(z.string()),
  preparedBy: nullableText,
  approvedBy: nullableText,
  reportingProcedures: nullableText,
  emergencyContacts: nullableText,
  lightweight: z.boolean().optional(),
  // CPP-only (web validator asserts this subset against the real request).
  principalDesigner: z.string().nullable().optional(),
  principalContractor: z.string().nullable().optional(),
  architectName: z.string().nullable().optional(),
  architectCompany: z.string().nullable().optional(),
  structuralEngineer: z.string().nullable().optional(),
  clientContactName: z.string().nullable().optional(),
  clientContactPhone: z.string().nullable().optional(),
  clientContactEmail: z.string().nullable().optional(),
  clientAddress: z.string().nullable().optional(),
  principalDesignerPhone: z.string().nullable().optional(),
  principalDesignerEmail: z.string().nullable().optional(),
  principalDesignerCompany: z.string().nullable().optional(),
  startDate: z.string().nullable().optional(),
  siteAccessArrangements: z.string().nullable().optional(),
  deliveryArrangements: z.string().nullable().optional(),
  publicProtection: z.string().nullable().optional(),
  siteHoarding: z.string().nullable().optional(),
  safetyArrangements: z.string().nullable().optional(),
  trainingRequirements: z.string().nullable().optional(),
  asbestosManagement: z.string().nullable().optional(),
  permitToWork: z.string().nullable().optional(),
  environmentalManagement: z.string().nullable().optional(),
  // RAMS-only (web validator asserts this subset against the real request).
  selectedWorkers: z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
        company: z.string().nullable().optional(),
        role: z.string(),
        phone: z.string().nullable().optional(),
        firstAidTrained: z.boolean(),
        fireWardenTrained: z.boolean().optional(),
      }),
    )
    .optional(),
  supervisorName: z.string().nullable().optional(),
}).strict();
export type CanonicalGenerationPayloadSubsetV1 = z.infer<typeof CanonicalGenerationPayloadSubsetV1Schema>;

/**
 * The project argument must come from the current, authoritative project
 * response. Draft answers cannot supply or overwrite project facts. This
 * function intentionally has no recovery/notes parameter or spread.
 */
export function buildCanonicalGenerationPayloadV1(
  draftInput: unknown,
  authoritativeProjectInput: unknown,
): CanonicalGenerationPayloadSubsetV1 {
  const draft = CanonicalGenerationDraftV1Schema.parse(draftInput);
  const confirmedProjectData = AuthoritativeProjectFactsV1Schema.parse(authoritativeProjectInput);
  const base = { confirmedProjectData, selectedHazardSlugs: [] as string[] };

  switch (draft.documentType) {
    case "COSHH":
      return CanonicalGenerationPayloadSubsetV1Schema.parse({
        ...base,
        preparedBy: draft.answers.preparedBy,
        approvedBy: draft.answers.approvedBy,
      });
    case "SITE_INDUCTION":
      return CanonicalGenerationPayloadSubsetV1Schema.parse({
        ...base, reportingProcedures: draft.answers.reportingProcedures,
      });
    case "EMERGENCY_PLAN":
      return CanonicalGenerationPayloadSubsetV1Schema.parse({
        ...base, emergencyContacts: draft.answers.emergencyContacts,
      });
    case "CPP":
      return CanonicalGenerationPayloadSubsetV1Schema.parse({
        ...base,
        ...draft.answers,
      });
    case "CPP_LIGHTWEIGHT":
      return CanonicalGenerationPayloadSubsetV1Schema.parse({
        ...base,
        lightweight: true,
        preparedBy: draft.answers.preparedBy,
      });
    case "RAMS":
      return CanonicalGenerationPayloadSubsetV1Schema.parse({
        ...base,
        selectedHazardSlugs: draft.answers.selectedHazardSlugs,
        selectedWorkers: draft.answers.selectedWorkers,
        supervisorName: draft.answers.supervisorName,
      });
  }
}
